import React, { ReactNode } from 'react';
import { useHighlight } from '@/hooks/shared/useHightlight';

export interface HighlightProps {
  text: string;
  searchTerm?: string;
  Wrapper?: React.ComponentType<{ children: React.ReactNode }>;
}

export const Highlight: React.FC<HighlightProps> = ({ text, searchTerm, Wrapper }) => {
  const highlightedContent = useHighlight({ text, searchTerm, Wrapper });
  return <>{highlightedContent}</>;
};

interface HighlightJSXProps {
  children: ReactNode;
  searchTerm?: string;
  Wrapper?: React.ComponentType<{ children: React.ReactNode }>;
}

export const HighlightJSX: React.FC<HighlightJSXProps> = ({ children, searchTerm, Wrapper }) => {
  if (!searchTerm) return <>{children}</>;

  const wrapperEl = (text: ReactNode, key: number) =>
    Wrapper ? (
      <Wrapper key={key}>{text}</Wrapper>
    ) : (
      <mark key={key} className="bg-yellow-300 rounded px-1">
        {text}
      </mark>
    );

  const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const highlightRecursively = (node: ReactNode): ReactNode => {
    if (typeof node === 'string') {
      const regex = new RegExp(`(${escapeRegExp(searchTerm)})`, 'gi');
      return node
        .split(regex)
        .map((part, i) => (part.toLowerCase() === searchTerm.toLowerCase() ? wrapperEl(part, i) : part));
    }

    if (React.isValidElement(node)) {
      return React.cloneElement(node, {
        ...node.props,
        children: highlightRecursively(node.props.children),
      });
    }

    if (Array.isArray(node)) {
      return node.map((child, i) => <React.Fragment key={i}>{highlightRecursively(child)}</React.Fragment>);
    }

    return node;
  };

  return <>{highlightRecursively(children)}</>;
};
